import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from '../api/axios';
import TagBadge from '../components/TagBadge';
import Pagination from '../components/Pagination';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import toast from 'react-hot-toast';

var safeDateFormat = function (dateStr) {
  if (!dateStr) return '—';
  var parts = dateStr.split('T')[0].split('-');
  var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return months[parseInt(parts[1], 10) - 1] + ' ' + parseInt(parts[2], 10) + ', ' + parts[0];
};

var sourceOptions = ['WhatsApp','Website','Referral','Social Media','Cold Call','Email Campaign','Other'];
var statusOptions = ['New','Contacted','Converted','Lost'];

var statusColors = {
  New: { bg: '#DBEAFE', text: '#1E40AF', border: '#93C5FD' },
  Contacted: { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' },
  Converted: { bg: '#D1FAE5', text: '#065F46', border: '#6EE7B7' },
  Lost: { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5' },
};

var emptyLead = { name: '', phone: '', email: '', source: 'Other', status: 'New', follow_up_date: '', tags: [] };

const Leads = () => {
  var navigate = useNavigate();
  var [searchParams, setSearchParams] = useSearchParams();
  var [leads, setLeads] = useState([]);
  var [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  var [loading, setLoading] = useState(true);
  var [search, setSearch] = useState(searchParams.get('search') || '');
  var [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  var [sourceFilter, setSourceFilter] = useState(searchParams.get('source') || '');
  var [showFilters, setShowFilters] = useState(false);
  var [showModal, setShowModal] = useState(false);
  var [editMode, setEditMode] = useState(false);
  var [formData, setFormData] = useState({ ...emptyLead });
  var [formLoading, setFormLoading] = useState(false);
  var [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  var [convertConfirm, setConvertConfirm] = useState({ open: false, id: null, name: '' });

  var fetchLeads = useCallback(async function (page) {
    if (!page) page = 1;
    setLoading(true);
    try {
      var params = { page: page, limit: 10 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      var res = await API.get('/leads', { params: params });
      setLeads(res.data.data.leads);
      setPagination(res.data.data.pagination);
    } catch (error) {
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, sourceFilter]);

  useEffect(function () {
    var timer = setTimeout(function () { fetchLeads(1); }, 300);
    return function () { clearTimeout(timer); };
  }, [fetchLeads]);

  // ✅ Status change handler — directly from dropdown in table
  var handleStatusChangeInline = async function (leadId, leadName, newStatus) {
    if (newStatus === 'Converted') {
      setConvertConfirm({ open: true, id: leadId, name: leadName });
      return;
    }
    try {
      await API.put('/leads/' + leadId + '/status', { status: newStatus });
      toast.success('Status → ' + newStatus);
      fetchLeads(pagination.page);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  var handleConvert = async function (id) {
    try {
      await API.post('/leads/' + id + '/convert');
      toast.success('Converted to customer!');
      fetchLeads(pagination.page);
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.message) || 'Failed');
    }
  };

  var handleDelete = async function (id) {
    try {
      await API.delete('/leads/' + id);
      toast.success('Lead deleted');
      fetchLeads(pagination.page);
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  var openAddModal = function () { setEditMode(false); setFormData({ ...emptyLead }); setShowModal(true); };
  var openEditModal = function (lead) {
    setEditMode(true);
    setFormData({
      id: lead.id, name: lead.name || '', phone: lead.phone || '', email: lead.email || '',
      source: lead.source || 'Other', status: lead.status || 'New',
      follow_up_date: lead.follow_up_date ? lead.follow_up_date.split('T')[0] : '', tags: lead.tags || [],
    });
    setShowModal(true);
  };

  var handleFormSubmit = async function (e) {
    e.preventDefault();
    setFormLoading(true);
    try {
      var payload = { name: formData.name, phone: formData.phone, email: formData.email, source: formData.source, status: formData.status, follow_up_date: formData.follow_up_date || null, tags: formData.tags };
      if (editMode) {
        await API.put('/leads/' + formData.id, payload);
        toast.success('Lead updated');
      } else {
        await API.post('/leads', payload);
        toast.success('Lead created');
      }
      setShowModal(false);
      fetchLeads(pagination.page);
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.message) || 'Failed');
    } finally {
      setFormLoading(false);
    }
  };

  var clearFilters = function () { setSearch(''); setStatusFilter(''); setSourceFilter(''); setSearchParams({}); };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
          <p className="text-gray-500 mt-1">Manage your leads and follow-ups</p>
        </div>
        <button onClick={openAddModal} className="btn-primary flex items-center gap-2 self-start">+ Add Lead</button>
      </div>

      {/* Search */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
            <input type="text" placeholder="Search by name, email, phone..." value={search} onChange={(e) => setSearch(e.target.value)} className="input-field pl-10" />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className={'btn-secondary flex items-center gap-2 ' + (showFilters ? 'bg-gray-100' : '')}>🔽 Filters</button>
        </div>
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex flex-col sm:flex-row gap-3">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-field sm:w-40">
              <option value="">All Statuses</option>
              {statusOptions.map(function (s) { return <option key={s} value={s}>{s}</option>; })}
            </select>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="input-field sm:w-40">
              <option value="">All Sources</option>
              {sourceOptions.map(function (s) { return <option key={s} value={s}>{s}</option>; })}
            </select>
            <button onClick={clearFilters} className="text-sm text-gray-500 hover:text-gray-700 font-medium">Clear</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#25D366' }}></div>
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-4xl mb-4">📋</div>
            <p className="text-gray-500 font-medium">No leads found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Phone</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Email</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Source</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Follow-up</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Tags</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase hidden xl:table-cell">Created</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {leads.map(function (lead) {
                  var sc = statusColors[lead.status] || statusColors.New;
                  return (
                    <tr key={lead.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">{lead.name}</p>
                        <p className="text-xs text-gray-500 md:hidden">{lead.phone}</p>
                        {lead.email && <p className="text-xs text-gray-500 md:hidden">{lead.email}</p>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 hidden md:table-cell">{lead.phone || '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 hidden lg:table-cell">{lead.email || '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 hidden lg:table-cell">{lead.source}</td>
                      {/* ✅ STATUS DROPDOWN */}
                      <td className="px-6 py-4">
                        <select
                          value={lead.status}
                          onChange={function (e) { handleStatusChangeInline(lead.id, lead.name, e.target.value); }}
                          className="text-xs font-semibold rounded-full px-3 py-1.5 border-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-300 appearance-none"
                          style={{
                            backgroundColor: sc.bg,
                            color: sc.text,
                            borderColor: sc.border,
                          }}
                        >
                          {statusOptions.map(function (s) {
                            return <option key={s} value={s}>{s}</option>;
                          })}
                        </select>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 hidden md:table-cell">{safeDateFormat(lead.follow_up_date)}</td>
                      <td className="px-6 py-4 hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {(lead.tags || []).map(function (tag, i) { return <TagBadge key={i} tag={tag} />; })}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 hidden xl:table-cell">{safeDateFormat(lead.created_at)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => navigate('/leads/' + lead.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition" title="View">👁</button>
                          <button onClick={() => openEditModal(lead)} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition" title="Edit">✏️</button>
                          <button onClick={() => setDeleteConfirm({ open: true, id: lead.id })} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition" title="Delete">🗑️</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && leads.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <Pagination pagination={pagination} onPageChange={function (p) { fetchLeads(p); }} />
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editMode ? 'Edit Lead' : 'Add New Lead'} size="lg">
        <form onSubmit={handleFormSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="input-field" required placeholder="Lead name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input type="text" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="input-field" placeholder="+91 98765 43210" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="input-field" placeholder="lead@example.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
              <select value={formData.source} onChange={(e) => setFormData({ ...formData, source: e.target.value })} className="input-field">
                {sourceOptions.map(function (s) { return <option key={s} value={s}>{s}</option>; })}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="input-field">
                {statusOptions.map(function (s) { return <option key={s} value={s}>{s}</option>; })}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Date</label>
              <input type="date" value={formData.follow_up_date} onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })} className="input-field" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={formLoading} className="btn-primary">{formLoading ? 'Saving...' : editMode ? 'Update Lead' : 'Add Lead'}</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog isOpen={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, id: null })} onConfirm={() => handleDelete(deleteConfirm.id)} title="Delete Lead" message="Delete this lead permanently?" confirmText="Delete" danger={true} />
      <ConfirmDialog isOpen={convertConfirm.open} onClose={() => setConvertConfirm({ open: false, id: null, name: '' })} onConfirm={() => handleConvert(convertConfirm.id)} title="Convert to Customer" message={'Convert "' + convertConfirm.name + '" to customer?'} confirmText="Convert" danger={false} />
    </div>
  );
};

export default Leads;